import { useCallback, useRef, useState } from 'react';
import { api } from './api.ts';
import { effectiveContentType, isAllowedAttachmentType, rejectionReason } from './attachmentTypes.ts';

export interface Attachment {
  id: number;
  name: string;
  contentType: string;
  state: 'uploading' | 'done' | 'error';
  url?: string;
  error?: string;
}

/**
 * Drag-and-drop and paste-to-attach for a plain-text message box.
 *
 * Neither a task nor a comment has an attachments field — both are just
 * text — so a finished upload does not get a slot of its own. It becomes a
 * removable pill under the message box instead, and only turns into a
 * markdown link — an image link for images, so it renders inline wherever
 * the text ends up, which for a comment includes the GitHub PR thread —
 * once `compose` is called at send time. Until then nothing has touched
 * what the person typed: what they paste is not what they have to read
 * around while they write the rest of the message.
 */
export function useAttachments({
  projectId,
  notify,
}: {
  projectId: string | undefined;
  notify: (title: string, options?: { body?: string; tone?: 'ok' | 'bad' }) => void;
}) {
  const [items, setItems] = useState<Attachment[]>([]);
  const seq = useRef(0);

  const upload = useCallback(
    async (file: File) => {
      if (!projectId) return;
      const id = ++seq.current;
      // html/svg/js get re-tagged to an inert type here — see
      // effectiveContentType — rather than rejected, so the upload below
      // still goes through and the person gets their file attached.
      const contentType = effectiveContentType(file.type || 'application/octet-stream');
      const name = file.name || 'attachment';
      if (!isAllowedAttachmentType(contentType)) {
        const message = rejectionReason(contentType);
        setItems((current) => [...current, { id, name, contentType, state: 'error', error: message }]);
        notify(`Could not attach ${name}`, { body: message, tone: 'bad' });
        setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 4000);
        return;
      }
      setItems((current) => [...current, { id, name, contentType, state: 'uploading' }]);
      try {
        const signed = await api.upload({
          project_id: projectId,
          filename: name,
          content_type: contentType,
        });
        const response = await fetch(signed.upload_url, {
          method: signed.method,
          headers: signed.headers,
          body: file,
        });
        if (!response.ok) throw new Error(`the upload failed (${response.status})`);
        setItems((current) =>
          current.map((item) => (item.id === id ? { ...item, state: 'done', url: signed.public_url } : item)),
        );
      } catch (error) {
        const message = (error as Error).message;
        setItems((current) =>
          current.map((item) => (item.id === id ? { ...item, state: 'error', error: message } : item)),
        );
        notify(`Could not attach ${name}`, { body: message, tone: 'bad' });
        setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 4000);
      }
    },
    [notify, projectId],
  );

  const uploadAll = useCallback(
    (files: Iterable<File>) => {
      for (const file of files) void upload(file);
    },
    [upload],
  );

  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  /** Puts a cleared set of pills back, for when the send they belonged to bounced. */
  const restore = useCallback((saved: Attachment[]) => setItems(saved), []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!event.dataTransfer.files?.length) return;
      event.preventDefault();
      uploadAll(event.dataTransfer.files);
    },
    [uploadAll],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    if (event.dataTransfer.types.includes('Files')) event.preventDefault();
  }, []);

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      if (files.length === 0) return;
      // A pasted file replaces the default paste — there is no text
      // representation of an image to fall back to anyway.
      event.preventDefault();
      uploadAll(files);
    },
    [uploadAll],
  );

  /**
   * The text actually sent: what was typed, plus a markdown link for every
   * finished upload, appended invisibly at send time rather than left for
   * the person to see or edit as raw markdown while composing.
   */
  const compose = useCallback(
    (text: string) => {
      const done = items.filter((item): item is Attachment & { url: string } => item.state === 'done' && !!item.url);
      if (done.length === 0) return text;
      const links = done
        .map((item) => (item.contentType.startsWith('image/') ? `![${item.name}](${item.url})` : `[${item.name}](${item.url})`))
        .join('\n');
      return text.length > 0 ? `${text}\n\n${links}` : links;
    },
    [items],
  );

  const uploading = items.some((item) => item.state === 'uploading');
  const hasAttachment = items.some((item) => item.state === 'done');

  return {
    items,
    onDrop,
    onDragOver,
    onPaste,
    attach: uploadAll,
    remove,
    clear,
    restore,
    compose,
    uploading,
    hasAttachment,
  };
}
